import { useRef } from 'react';
import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import {
  Alert,
  Badge,
  Button,
  ConfirmButton,
  ContentBox,
  FlexContainer,
  MoreOptions
} from 'components/common';
import {
  ColorPicker,
  Form,
  FormGroup,
  FormSection,
  ImageUploader,
  NumberStepper,
  Text
} from 'components/common/form';
import {
  SiteSettingsThemesEditHighlight,
  SiteSettingsThemesExport,
  SiteSettingsThemesImport
} from '.';

import type { FormApi } from 'informed';
import type { Theme } from 'shared-types';

interface Props {
  onSave: () => void;
  siteTheme: Theme;
  siteThemeId: number;
  title: string;
}

const SiteSettingsThemesEditorSite = ({
  onSave,
  siteTheme,
  siteThemeId,
  title
}: Props) => {
  const formApi = useRef<FormApi>(undefined);
  const emailLogoUploader = useRef<any>(undefined);
  const loginBackgroundImageUploader = useRef<any>(undefined);
  const logoUploader = useRef<any>(undefined);
  const mobileLogoUploader = useRef<any>(undefined);

  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const handleChange = async (values) => {
    try {
      setTimeout(() => {
        dispatch(
          actions.updateTheme(siteThemeId, {
            ...siteTheme,
            ...values.data
          })
        );
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const handleSubmit = async () => {
    const values = formApi.current.getState().values as any;

    if (logoUploader.current?.state.imageAdded) {
      const file = await logoUploader.current.upload();
      if (file) values.data.logo = file.url;
    }

    if (mobileLogoUploader.current?.state.imageAdded) {
      const file = await mobileLogoUploader.current.upload();
      if (file) values.data.mobileLogo = file.url;
    }

    if (emailLogoUploader.current?.state.imageAdded) {
      const file = await emailLogoUploader.current.upload();
      if (file) values.data.emailLogo = file.url;
    }

    if (loginBackgroundImageUploader.current?.state.imageAdded) {
      const file = await loginBackgroundImageUploader.current.upload();
      if (file) values.data.loginBackgroundImage = file.url;
    }

    await api.put(`/admin/server-themes/${siteThemeId}`, values);
    await onSave();

    dispatch.addNotice({
      text: messages.siteThemesUpdateSuccess,
      type: 'success'
    });
    return true;
  };

  return (
    <Form
      getApi={(api) => (formApi.current = api)}
      initialValues={{
        title,
        data: {
          ...siteTheme,
          certificateLogoWidth: siteTheme.certificateLogoWidth || 150,
          mobileLogoWidth: siteTheme.mobileLogoWidth || 132
        }
      }}
      onValueChange={handleChange}
      style={{ height: '100%' }}
    >
      {({ formState }) => (
        <FlexContainer style={{ height: '100%', flexDirection: 'column' }}>
          <div style={{ flexGrow: 1, overflowY: 'auto', width: '100%' }}>
            <FormSection>
              <FormGroup label={messages.commonTitle}>
                <Text field="title" style={{ maxWidth: '100%', width: 300 }} />
              </FormGroup>
            </FormSection>

            <FormSection isExpandable={true} title={messages.commonLogo}>
              <FormGroup label={messages.commonLogo}>
                <ImageUploader
                  field="data.logo"
                  id="custom-theme-logo"
                  maxWidth={formState.values.data?.logoWidth}
                  messages={messages}
                  setUploader={(imageUploader) =>
                    (logoUploader.current = imageUploader)
                  }
                  showPreviewImage={true}
                />
              </FormGroup>

              <FormGroup>
                <NumberStepper
                  field="data.logoWidth"
                  id="custom-theme-logo-width"
                  label="logoWidth"
                  min={0}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />
              </FormGroup>

              <FormGroup>
                <NumberStepper
                  field="data.certificateLogoWidth"
                  id="custom-theme-certificate-logo-width"
                  label="certificateLogoWidth"
                  min={0}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />
              </FormGroup>

              <FormGroup label="mobileLogo">
                <ImageUploader
                  field="data.mobileLogo"
                  id="custom-theme-mobile-logo"
                  maxWidth={formState.values.data?.mobileLogoWidth}
                  messages={messages}
                  setUploader={(imageUploader) =>
                    (mobileLogoUploader.current = imageUploader)
                  }
                  showPreviewImage={true}
                />
              </FormGroup>

              <FormGroup>
                <NumberStepper
                  field="data.mobileLogoWidth"
                  id="custom-theme-mobile-logo-width"
                  label="mobileLogoWidth"
                  min={0}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />
              </FormGroup>

              <FormGroup label="emailLogo">
                <ImageUploader
                  field="data.emailLogo"
                  id="custom-theme-email-logo"
                  maxWidth={formState.values.data?.emailLogoWidth}
                  messages={messages}
                  setUploader={(imageUploader) =>
                    (emailLogoUploader.current = imageUploader)
                  }
                  showPreviewImage={true}
                />
              </FormGroup>

              <FormGroup label="emailLogoAlign">
                <Text
                  field="data.emailLogoAlign"
                  style={{ maxWidth: '100%', width: 160 }}
                />
              </FormGroup>

              <FormGroup>
                <NumberStepper
                  field="data.emailLogoWidth"
                  id="custom-theme-email-logo-width"
                  label="emailLogoWidth"
                  min={0}
                  stepValue={1}
                  style={{ marginTop: 10 }}
                />
              </FormGroup>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Common">
              <FormGroup label="linkColor">
                <ColorPicker field="data.linkColor" />
              </FormGroup>

              <FormGroup label="certificateHighlightColor">
                <ColorPicker field="data.certificateHighlightColor" />
              </FormGroup>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Login">
              <FormGroup label="loginBackgroundColor">
                <ColorPicker field="data.loginBackgroundColor" />
              </FormGroup>

              <FormGroup label="loginBackgroundImage">
                <ImageUploader
                  field="data.loginBackgroundImage"
                  id="custom-theme-login-background-image"
                  maxWidth={200}
                  messages={messages}
                  setUploader={(imageUploader) =>
                    (loginBackgroundImageUploader.current = imageUploader)
                  }
                  showPreviewImage={true}
                />
              </FormGroup>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Header">
              <FormGroup label="headerBackgroundColor">
                <ColorPicker field="data.headerBackgroundColor" />
              </FormGroup>

              <FormGroup label="headerButtonBackgroundColor">
                <ColorPicker field="data.headerButtonBackgroundColor" />
              </FormGroup>

              <FormGroup label="headerButtonBackgroundColorHover">
                <ColorPicker field="data.headerButtonBackgroundColorHover" />
              </FormGroup>

              <FormGroup label="headerIconColor">
                <ColorPicker field="data.headerIconColor" />
              </FormGroup>

              <FormGroup label="headerTextColor">
                <ColorPicker field="data.headerTextColor" />
              </FormGroup>

              <FormGroup label="headerPadding">
                <Text field="data.headerPadding" style={{ maxWidth: '100%' }} />
              </FormGroup>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="SubHeader">
              <FormGroup label="subHeaderBackgroundColor">
                <ColorPicker field="data.subHeaderBackgroundColor" />
              </FormGroup>

              <FormGroup label="subHeaderColorActive">
                <ColorPicker field="data.subHeaderColorActive" />
              </FormGroup>

              <FormGroup label="subHeaderColor">
                <ColorPicker field="data.subHeaderColor" />
              </FormGroup>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Page">
              <FormGroup label="pageBackgroundColor">
                <ColorPicker field="data.pageBackgroundColor" />
              </FormGroup>

              <FormGroup label="pageTextColor">
                <ColorPicker field="data.pageTextColor" />
              </FormGroup>

              <FormGroup label="pageTextColorLighter">
                <ColorPicker field="data.pageTextColorLighter" />
              </FormGroup>

              <FormGroup label="pageIconBackgroundColor">
                <ColorPicker field="data.pageIconBackgroundColor" />
              </FormGroup>

              <FormGroup label="pageIconTextColor">
                <ColorPicker field="data.pageIconTextColor" />
              </FormGroup>

              <FormGroup label="pageIconTextColorLighter">
                <ColorPicker field="data.pageIconTextColorLighter" />
              </FormGroup>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Main">
              <FormGroup label="mainColor">
                <ColorPicker field="data.mainColor" />
              </FormGroup>

              <FormGroup label="mainColorActive">
                <ColorPicker field="data.mainColorActive" />
              </FormGroup>

              <FormGroup label="mainTextColor">
                <ColorPicker field="data.mainTextColor" />
              </FormGroup>

              <div className="preview">
                <div className="preview-item">
                  <Button
                    customBackgroundColor={formState.values.data?.mainColor}
                    customBackgroundColorHover={
                      formState.values.data?.mainColorActive
                    }
                    customColor={formState.values.data?.mainTextColor}
                    text="Button"
                  />
                </div>
              </div>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Success">
              <FormGroup label="successColor">
                <ColorPicker field="data.successColor" />
              </FormGroup>

              <FormGroup label="successColorActive">
                <ColorPicker field="data.successColorActive" />
              </FormGroup>

              <FormGroup label="successTextColor">
                <ColorPicker field="data.successTextColor" />
              </FormGroup>

              <FormGroup label="successColorDark">
                <ColorPicker field="data.successColorDark" />
              </FormGroup>

              <FormGroup label="successColorDarkActive">
                <ColorPicker field="data.successColorDarkActive" />
              </FormGroup>

              <FormGroup label="successTextColorDark">
                <ColorPicker field="data.successTextColorDark" />
              </FormGroup>

              <FormGroup label="successLightColor">
                <ColorPicker field="data.successLightColor" />
              </FormGroup>

              <FormGroup label="successLightTextColor">
                <ColorPicker field="data.successLightTextColor" />
              </FormGroup>

              <FormGroup label="successStandaloneColor">
                <ColorPicker field="data.successStandaloneColor" />
              </FormGroup>

              <div className="preview">
                <div className="preview-item">
                  <Button
                    category="success"
                    customBackgroundColor={formState.values.data?.successColor}
                    customBackgroundColorHover={
                      formState.values.data?.successColorActive
                    }
                    customColor={formState.values.data?.successTextColor}
                    text="Button"
                  />
                </div>
                <div className="preview-item">
                  <div className="preview-item-inline">
                    <Badge
                      category="success"
                      text="Badge"
                      style={{
                        color: formState.values.data?.successTextColor,
                        backgroundColor: formState.values.data?.successColor
                      }}
                    />
                  </div>
                  <div className="preview-item-inline">
                    <Badge
                      category="success-light"
                      text="Badge"
                      style={{
                        color: formState.values.data?.successLightTextColor,
                        backgroundColor:
                          formState.values.data?.successLightColor
                      }}
                    />
                  </div>
                </div>
                <div className="preview-item">
                  <div className="preview-item-inline">
                    <Alert
                      type="success-light"
                      text="Lorem ipsum dolor sit amet"
                      style={{
                        color: formState.values.data?.successLightTextColor,
                        backgroundColor:
                          formState.values.data?.successLightColor
                      }}
                    />
                  </div>
                </div>
              </div>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Danger">
              <FormGroup label="dangerColor">
                <ColorPicker field="data.dangerColor" />
              </FormGroup>

              <FormGroup label="dangerColorActive">
                <ColorPicker field="data.dangerColorActive" />
              </FormGroup>

              <FormGroup label="dangerTextColor">
                <ColorPicker field="data.dangerTextColor" />
              </FormGroup>

              <FormGroup label="dangerLightColor">
                <ColorPicker field="data.dangerLightColor" />
              </FormGroup>

              <FormGroup label="dangerLightTextColor">
                <ColorPicker field="data.dangerLightTextColor" />
              </FormGroup>

              <div className="preview">
                <div className="preview-item">
                  <Button
                    category="danger"
                    customBackgroundColor={formState.values.data?.dangerColor}
                    customBackgroundColorHover={
                      formState.values.data?.dangerColorActive
                    }
                    customColor={formState.values.data?.dangerTextColor}
                    text="Button"
                  />
                </div>
                <div className="preview-item">
                  <div className="preview-item-inline">
                    <Badge
                      category="error"
                      text="Badge"
                      style={{
                        color: formState.values.data?.dangerTextColor,
                        backgroundColor: formState.values.data?.dangerColor
                      }}
                    />
                  </div>
                  <div className="preview-item-inline">
                    <Badge
                      category="error-light"
                      text="Badge"
                      style={{
                        color: formState.values.data?.dangerLightTextColor,
                        backgroundColor: formState.values.data?.dangerLightColor
                      }}
                    />
                  </div>
                </div>
                <div className="preview-item">
                  <div className="preview-item-inline">
                    <Alert
                      type="error-light"
                      text="Lorem ipsum dolor sit amet"
                      style={{
                        color: formState.values.data?.dangerLightTextColor,
                        backgroundColor: formState.values.data?.dangerLightColor
                      }}
                    />
                  </div>
                </div>
              </div>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Warning">
              <FormGroup label="warningColor">
                <ColorPicker field="data.warningColor" />
              </FormGroup>

              <FormGroup label="warningTextColor">
                <ColorPicker field="data.warningTextColor" />
              </FormGroup>

              <FormGroup label="warningLightColor">
                <ColorPicker field="data.warningLightColor" />
              </FormGroup>

              <FormGroup label="warningLightTextColor">
                <ColorPicker field="data.warningLightTextColor" />
              </FormGroup>

              <FormGroup label="warningStandaloneColor">
                <ColorPicker field="data.warningStandaloneColor" />
              </FormGroup>

              <div className="preview">
                <div className="preview-item">
                  <div className="preview-item-inline">
                    <Badge
                      category="warning"
                      text="Badge"
                      style={{
                        color: formState.values.data?.warningTextColor,
                        backgroundColor: formState.values.data?.warningColor
                      }}
                    />
                  </div>
                  <div className="preview-item-inline">
                    <Badge
                      category="warning-light"
                      text="Badge"
                      style={{
                        color: formState.values.data?.warningLightTextColor,
                        backgroundColor:
                          formState.values.data?.warningLightColor
                      }}
                    />
                  </div>
                </div>
                <div className="preview-item">
                  <div className="preview-item-inline">
                    <Alert
                      type="warning-light"
                      text="Lorem ipsum dolor sit amet"
                      style={{
                        color: formState.values.data?.warningLightTextColor,
                        backgroundColor:
                          formState.values.data?.warningLightColor
                      }}
                    />
                  </div>
                </div>
              </div>
            </FormSection>

            <FormSection isExpandable={true} maxWidth="100%" title="Grey">
              <FormGroup label="lightGreyLighter">
                <ColorPicker field="data.lightGreyLighter" />
              </FormGroup>
              <FormGroup label="lightGrey">
                <ColorPicker field="data.lightGrey" />
              </FormGroup>
              <FormGroup label="lightGreyActive">
                <ColorPicker field="data.lightGreyActive" />
              </FormGroup>
              <FormGroup label="lightGreyDark">
                <ColorPicker field="data.lightGreyDark" />
              </FormGroup>
              <FormGroup label="lightGreyDarkActive">
                <ColorPicker field="data.lightGreyDarkActive" />
              </FormGroup>

              <div className="preview">
                <div className="preview-item">
                  <div className="preview-item-inline">
                    <Button
                      category="lighter"
                      customBackgroundColor={formState.values.data?.lightGrey}
                      customBackgroundColorHover={
                        formState.values.data?.lightGreyActive
                      }
                      text="Light grey"
                    />
                  </div>
                  <div className="preview-item-inline">
                    <Button
                      category="light"
                      customBackgroundColor={
                        formState.values.data?.lightGreyDark
                      }
                      customBackgroundColorHover={
                        formState.values.data?.lightGreyDarkActive
                      }
                      text="Light grey dark"
                    />
                  </div>
                </div>
                <div className="preview-item">
                  <div className="preview-item-inline">
                    <Alert
                      type="info"
                      text="Lorem ipsum dolor sit amet"
                      style={{
                        backgroundColor: formState.values.data?.lightGrey
                      }}
                    />
                  </div>
                </div>
                <div className="preview-item">
                  <ContentBox
                    style={{
                      backgroundColor: formState.values.data?.lightGrey,
                      display: 'inline-block'
                    }}
                  >
                    <div className="preview-item-inline">
                      <Button
                        category="light"
                        customBackgroundColor={
                          formState.values.data?.lightGreyDark
                        }
                        customBackgroundColorHover={
                          formState.values.data?.lightGreyDarkActive
                        }
                        text="Light grey dark"
                      />
                    </div>
                    <div className="preview-item-inline">
                      <Alert
                        type="info-dark"
                        text="Lorem ipsum dolor sit amet"
                        style={{
                          backgroundColor: formState.values.data?.lightGreyDark
                        }}
                      />
                    </div>
                  </ContentBox>
                </div>
              </div>
            </FormSection>

            <FormSection
              isExpandable={true}
              maxWidth="100%"
              title="Text (not ready yet)"
            >
              <FormGroup label="textColor">
                <ColorPicker field="data.textColor" />
              </FormGroup>

              <FormGroup label="textColorLight">
                <ColorPicker field="data.textColorLight" />
              </FormGroup>

              <FormGroup label="textColorLighter">
                <ColorPicker field="data.textColorLighter" />
              </FormGroup>
            </FormSection>

            <SiteSettingsThemesEditHighlight
              field="data.textHighlightColors"
              formState={formState}
            />

            <FormSection isExpandable={true} maxWidth="100%" title="Card">
              <FormGroup label="cardBoxShadow">
                <Text field="data.cardBoxShadow" style={{ maxWidth: '100%' }} />
              </FormGroup>

              <FormGroup label="cardBoxShadowActive">
                <Text
                  field="data.cardBoxShadowActive"
                  style={{ maxWidth: '100%' }}
                />
              </FormGroup>
            </FormSection>
          </div>

          <div
            style={{
              borderTop: '1px solid #dcdddf',
              padding: 15,
              width: '100%'
            }}
          >
            <FlexContainer style={{ justifyContent: 'space-between' }}>
              <ConfirmButton
                actionText={messages.commonSaveChanges}
                buttonText={messages.commonSaveChanges}
                category="success"
                confirmText={
                  'Are you sure you want to save changes to the theme?'
                }
                confirmTitle={messages.commonSaveChanges}
                onConfirm={handleSubmit}
              />

              <MoreOptions alignVertical="bottom" category="lighter">
                <SiteSettingsThemesImport
                  onImport={onSave}
                  themeId={siteThemeId}
                />
                <SiteSettingsThemesExport
                  value={JSON.stringify(formState.values.data)}
                />
              </MoreOptions>
            </FlexContainer>
          </div>
        </FlexContainer>
      )}
    </Form>
  );
};

export default SiteSettingsThemesEditorSite;
